import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import TeroumaLogo from "./ui/TeroumaLogo";

/**
 * En-tête « site » des pages publiques partagées par lien (campagnes…).
 *
 * Objectif (demande d'Avi, référence AlloDons) : quand un donateur ouvre un
 * lien de campagne, il doit se croire SUR le site Terouma — logo, navigation
 * complète, espaces donateur/gestionnaire — et non sur une page orpheline.
 * Sur téléphone : menu hamburger → panneau navy déroulant (mêmes codes que
 * la page d'accueil, pour que la transition site ↔ campagne soit invisible).
 *
 * Volontairement autonome (aucune prop requise) pour se monter en une ligne
 * sur n'importe quelle page publique.
 */
const NAV_LINKS = [
  { label: "Accueil", to: "/" },
  { label: "Synagogues", to: "/organisations" },
  { label: "Campagnes", to: "/campagnes" },
  { label: "Kiddoush", to: "/kiddoush" },
  { label: "Contact", to: "/contact" },
];

export default function PublicSiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Ombre discrète dès que la page défile — l'en-tête « décolle » du contenu.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      className={`sticky top-0 z-50 bg-white/92 backdrop-blur border-b border-stone-200 transition-shadow duration-300 ${
        scrolled ? "shadow-[0_8px_30px_-18px_rgba(13,27,51,0.45)]" : ""
      }`}
    >
      <style>{`@keyframes psh-slide{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 md:h-[72px] flex items-center justify-between gap-3">
        <Link to="/" className="shrink-0" aria-label="Terouma — accueil">
          <div className="md:hidden">
            <TeroumaLogo size={34} variant="full" textColor="#0D1B33" />
          </div>
          <div className="hidden md:block">
            <TeroumaLogo size={40} variant="full" textColor="#0D1B33" />
          </div>
        </Link>

        {/* Desktop : navigation complète */}
        <div className="hidden md:flex items-center gap-6 text-sm text-stone-600">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="hover:text-stone-900 transition-colors">
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-2">
            <Link
              to="/mon-espace"
              className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full border-2 transition-colors hover:opacity-90"
              style={{ borderColor: "#B8962E", color: "#B8962E" }}
            >
              Espace donateur
            </Link>
            <Link
              to="/login"
              className="text-sm font-medium px-4 py-2 rounded-full transition-colors hover:opacity-90"
              style={{ background: "#0D1B33", color: "#FFFFFF" }}
            >
              Espace synagogue
            </Link>
          </div>
          {/* Mobile : les trois petits traits */}
          <button
            type="button"
            aria-label="Menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            className="md:hidden h-10 w-10 -mr-1 inline-flex items-center justify-center"
            style={{ color: "#0D1B33" }}
          >
            {menuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>

      {/* Panneau mobile déroulant — mêmes codes que la page d'accueil */}
      {menuOpen && (
        <div
          className="md:hidden border-t border-white/10 shadow-2xl"
          style={{ background: "#0D1B33", animation: "psh-slide .18s ease-out" }}
        >
          <div className="px-5 py-2 flex flex-col">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className="py-3 text-[15px] text-white/90 border-b border-white/10"
              >
                {l.label}
              </Link>
            ))}
            <div className="flex gap-2 pt-4 pb-2">
              <Link
                to="/mon-espace"
                onClick={() => setMenuOpen(false)}
                className="flex-1 text-center text-sm font-medium py-2.5 rounded-full border-2"
                style={{ borderColor: "#B8962E", color: "#E7C76A" }}
              >
                Espace donateur
              </Link>
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="flex-1 text-center text-sm font-medium py-2.5 rounded-full"
                style={{ background: "#FFFFFF", color: "#0D1B33" }}
              >
                Espace synagogue
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
